/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState, useContext } from 'react';
import API from '../api/api';
import { AuthContext } from '../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import SavingsIcon from '@mui/icons-material/Savings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const formatCurrency = (value, lang) => {
  if (value == null) return '-';
  return new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const RevenueChart = ({ series, lang, tr }) => {
  if (!series || series.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', color: 'var(--text-muted)' }}>
        {tr('Aucune donnee', 'لا توجد بيانات')}
      </Box>
    );
  }

  const values = series.map((s) => s.amount || 0);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;

  const points = series.map((item, index) => {
    const x = series.length === 1 ? 50 : (index / (series.length - 1)) * 100;
    const y = 38 - ((item.amount - min) / span) * 30;
    return { x, y };
  });

  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  const firstLabel = series[0]?.period || '';
  const lastLabel = series[series.length - 1]?.period || '';

  return (
    <Box sx={{ width: '100%', height: 200 }}>
      <svg viewBox="0 0 100 46" width="100%" height="100%" preserveAspectRatio="none">
        <defs>
          <linearGradient id="cashLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#16a34a" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
        </defs>
        <line x1="6" y1="6" x2="6" y2="38" stroke="var(--border-subtle)" strokeWidth="0.5" />
        <line x1="6" y1="38" x2="98" y2="38" stroke="var(--border-subtle)" strokeWidth="0.5" />
        <path
          d={path}
          fill="none"
          stroke="url(#cashLine)"
          strokeWidth="2.5"
        />
        {points.map((point, index) => (
          <circle
            key={series[index].period}
            cx={point.x}
            cy={point.y}
            r="1.5"
            fill="var(--text-secondary)"
          />
        ))}
        <text x="0" y="8" fill="var(--text-muted)" fontSize="3">
          {formatCurrency(max, lang)}
        </text>
        <text x="0" y="38" fill="var(--text-muted)" fontSize="3">
          {formatCurrency(min, lang)}
        </text>
        <text x="6" y="45" fill="var(--text-muted)" fontSize="3">
          {firstLabel}
        </text>
        <text x="70" y="45" fill="var(--text-muted)" fontSize="3">
          {lastLabel}
        </text>
      </svg>
    </Box>
  );
};

export default function Cash() {
  const { user } = useContext(AuthContext);
  const { showSnackbar } = useSnackbar();
  const { lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [receivables, setReceivables] = useState(0);
  const [range, setRange] = useState('day');
  const [selectedDate, setSelectedDate] = useState(getTodayIsoDate());
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [revenueTotal, setRevenueTotal] = useState(0);
  const [cashForm, setCashForm] = useState({ type: 'IN', amount: '', note: '' });
  const [journal, setJournal] = useState([]);
  const [journalFilter, setJournalFilter] = useState('all');
  const [openExpenses, setOpenExpenses] = useState([]);
  const [selectedExpenseId, setSelectedExpenseId] = useState('');
  const isAdmin = String(user?.role || '').toUpperCase() === 'ADMIN';

  const fetchCash = async () => {
    try {
      setLoading(true);
      const res = await API.get('/cash');
      setBalance(res.data.balance || 0);
      setReceivables(res.data.receivables || 0);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement de la caisse', 'فشل تحميل الصندوق'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchJournal = async () => {
    try {
      const res = await API.get('/cash/journal', {
        params: { filter: journalFilter },
      });
      setJournal(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement du journal', 'فشل تحميل السجل'), 'error');
    }
  };

  const fetchRevenue = async () => {
    try {
      const res = await API.get('/cash/revenue', {
        params: { range, date: selectedDate },
      });
      setRevenueSeries(res.data.series || []);
      setRevenueTotal(res.data.total || 0);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr("Erreur lors du chargement du chiffre d'affaires", 'فشل تحميل الإيراد'), 'error');
    }
  };

  const fetchOpenExpenses = async () => {
    try {
      const res = await API.get('/expenses/open');
      setOpenExpenses(res.data || []);
      if (res.data.length > 0) {
        setSelectedExpenseId((prev) => prev || String(res.data[0].id));
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du chargement des depenses', 'فشل تحميل المصاريف'), 'error');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchCash();
    fetchJournal();
    fetchOpenExpenses();
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRevenue();
  }, [range, selectedDate]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchJournal();
  }, [journalFilter]);

  const handleCashChange = (e) =>
    setCashForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCashSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/cash/transaction', {
        type: cashForm.type,
        amount: Number(cashForm.amount),
        note: cashForm.note,
      });
      setCashForm({ type: cashForm.type, amount: '', note: '' });
      showSnackbar(tr('Transaction enregistree', 'تم تسجيل العملية'), 'success');
      fetchCash();
      fetchJournal();
      fetchRevenue();
      fetchOpenExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors de la transaction', 'فشل العملية'), 'error');
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await API.get('/cash/transactions.csv', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cash-transactions.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr("Erreur lors de l'export", 'فشل التصدير'), 'error');
    }
  };

  const chartTitle = useMemo(() => {
    const map = {
      day: tr('Jour', 'يوم'),
      week: tr('Semaine', 'أسبوع'),
      month: tr('Mois', 'شهر'),
      year: tr('Annee', 'سنة'),
    };
    return map[range] || tr('Jour', 'يوم');
  }, [range, lang]);

  const handlePayExpense = async () => {
    if (!selectedExpenseId) return;
    try {
      await API.post(`/expenses/${selectedExpenseId}/pay`);
      showSnackbar(tr('Depense payee', 'تم دفع المصروف'), 'success');
      fetchCash();
      fetchJournal();
      fetchRevenue();
      fetchOpenExpenses();
    } catch (err) {
      showSnackbar(err.response?.data?.message || tr('Erreur lors du paiement', 'فشل الدفع'), 'error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 6 },
        py: 5,
        background: 'var(--app-bg)',
      }}
    >
      <Box
        sx={{
          mb: 5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--text-primary)',
            letterSpacing: 1,
            fontSize: { xs: 28, sm: 36, md: 42 },
          }}
        >
          {tr('Caisse digitale', 'الصندوق الرقمي')}
        </Typography>
        <Typography sx={{ color: 'var(--text-muted)', mt: 1, maxWidth: 520 }}>
          {tr('Suivi du cash, encaissements et sorties.', 'متابعة الصندوق، التحصيلات والمصروفات.')}
        </Typography>
      </Box>

      {loading ? (
        <Stack alignItems="center">
          <CircularProgress sx={{ color: 'var(--text-primary)' }} />
        </Stack>
      ) : (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={4}>
            <Paper
              sx={{
                flex: 1,
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(22,163,74,0.24), rgba(16,185,129,0.14))',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(34,197,94,0.2)',
                    color: '#16a34a',
                  }}
                >
                  <SavingsIcon />
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                    {tr('Solde actuel', 'الرصيد الحالي')}
                  </Typography>
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 24 }}>
                    {formatCurrency(balance, lang)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              sx={{
                flex: 1,
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(249,115,22,0.2), rgba(251,191,36,0.12))',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(249,115,22,0.2)',
                    color: '#fb923c',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                    {tr('Creances internes', 'الذمم الداخلية')}
                  </Typography>
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 24 }}>
                    {formatCurrency(receivables, lang)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              sx={{
                flex: 1,
                p: 3,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(22,163,74,0.2), rgba(5,150,105,0.12))',
                border: '1px solid var(--border-subtle)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(22,163,74,0.2)',
                    color: '#16a34a',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                    {tr("Chiffre d'affaires", 'الإيراد')} ({chartTitle})
                  </Typography>
                  <Typography sx={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: 24 }}>
                    {formatCurrency(revenueTotal, lang)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>

          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  size="small"
                  label={tr('Periode', 'الفترة')}
                  value={range}
                  onChange={(e) => setRange(e.target.value)}
                  sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 160 }}
                >
                  <MenuItem value="day">{tr('Jour', 'يوم')}</MenuItem>
                  <MenuItem value="week">{tr('Semaine', 'أسبوع')}</MenuItem>
                  <MenuItem value="month">{tr('Mois', 'شهر')}</MenuItem>
                  <MenuItem value="year">{tr('Annee', 'سنة')}</MenuItem>
                </TextField>
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                />
              </Stack>
              <Typography sx={{ color: 'var(--text-muted)', fontWeight: 700 }}>
                {tr('Courbe des variations', 'منحنى التغيرات')}
              </Typography>
            </Stack>
            <Divider sx={{ my: 2, opacity: 0.2 }} />
            <RevenueChart series={revenueSeries} lang={lang} tr={tr} />
          </Paper>

          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(22,163,74,0.18), rgba(16,185,129,0.1))',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
              {tr('Mouvement de caisse', 'حركة الصندوق')}
            </Typography>
            <Box component="form" onSubmit={handleCashSubmit}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  name="type"
                  label={tr('Type', 'النوع')}
                  value={cashForm.type}
                  onChange={handleCashChange}
                  required
                  fullWidth
                  disabled={!isAdmin}
                  sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                >
                  <MenuItem value="IN">{tr('Entree', 'دخول')}</MenuItem>
                  <MenuItem value="OUT">{tr('Sortie', 'خروج')}</MenuItem>
                </TextField>
                <TextField
                  name="amount"
                  label={tr('Montant (EUR)', 'المبلغ (EUR)')}
                  type="number"
                  value={cashForm.amount}
                  onChange={handleCashChange}
                  required
                  fullWidth
                  disabled={!isAdmin}
                  sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                />
                <TextField
                  name="note"
                  label={tr('Note', 'ملاحظة')}
                  value={cashForm.note}
                  onChange={handleCashChange}
                  fullWidth
                  disabled={!isAdmin}
                  sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!isAdmin}
                  sx={{
                    px: 4,
                    borderRadius: 3,
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #16a34a, #059669)',
                  }}
                >
                  {tr('Enregistrer', 'حفظ')}
                </Button>
              </Stack>
            </Box>
          </Paper>

          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(239,68,68,0.16), rgba(5,150,105,0.1))',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700, mb: 2 }}>
              {tr('Payer une depense ouverte', 'دفع مصروف مفتوح')}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                select
                label={tr('Depense', 'المصروف')}
                value={selectedExpenseId}
                onChange={(e) => setSelectedExpenseId(e.target.value)}
                fullWidth
                disabled={!isAdmin || openExpenses.length === 0}
                sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2 }}
              >
                {openExpenses.map((exp) => (
                  <MenuItem key={exp.id} value={String(exp.id)}>
                    {exp.name} - {formatCurrency(exp.amount, lang)} ({exp.category})
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={handlePayExpense}
                disabled={!isAdmin || !selectedExpenseId}
                sx={{
                  px: 4,
                  borderRadius: 3,
                  fontWeight: 700,
                  background: 'linear-gradient(90deg, #ef4444, #f97316)',
                }}
              >
                {tr('Payer', 'دفع')}
              </Button>
            </Stack>
          </Paper>

          <Paper
            sx={{
              borderRadius: 4,
              background: 'var(--surface-1)',
              border: '1px solid var(--border-subtle)',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  {tr('Journal (cash + demandes)', 'السجل (الصندوق + الطلبات)')}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    value={journalFilter}
                    onChange={(e) => setJournalFilter(e.target.value)}
                    sx={{ backgroundColor: 'var(--input-bg)', borderRadius: 2, minWidth: 160 }}
                  >
                    <MenuItem value="all">{tr('Tout', 'الكل')}</MenuItem>
                    <MenuItem value="cash">{tr('Cash', 'نقد')}</MenuItem>
                    <MenuItem value="receivables">{tr('Creances', 'ذمم')}</MenuItem>
                  </TextField>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExportCsv}
                    disabled={!isAdmin}
                    sx={{
                      borderRadius: 999,
                      borderColor: 'var(--border-subtle)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {tr('Export CSV', 'تصدير CSV')}
                  </Button>
                </Stack>
              </Stack>
            </Box>
            <TableContainer sx={{ maxHeight: { xs: 360, md: 480 } }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[tr('Type', 'النوع'), tr('Montant', 'المبلغ'), tr('Note', 'ملاحظة'), tr('Date', 'التاريخ')].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          color: 'var(--text-primary)',
                          fontWeight: 700,
                          backgroundColor: 'var(--table-header-bg)',
                          borderBottom: '1px solid var(--border-subtle)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {journal.map((row) => (
                    <TableRow
                      key={`${row.source}-${row.id}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'var(--surface-3)',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          color: row.source === 'RECEIVABLE' ? '#fb923c' : 'var(--text-primary)',
                          fontWeight: 700,
                        }}
                      >
                        {row.source === 'RECEIVABLE' ? tr('CHARGE', 'ذمة') : row.type}
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)' }}>
                        {formatCurrency(row.amount, lang)}
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)' }}>
                        {row.note || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'var(--text-secondary)' }}>
                        {row.created_at}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}












